import * as THREE from "three";
import Experience from "../../Experience.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import debug from "debug";

const log = debug("app:env");

/**
 * Manages the lighting and environment settings in a Three.js scene.
 * Handles switching between 3D and 2D environments dynamically.
 * @category Experience
 * @subcategory World
 */
export default class Environment {
  /**
   * Constructs the Environment class.
   * @param {Object} object - Contains viewer configuration, including environment map URLs.
   */
  constructor(object) {
    /**
     * Experience instance for accessing scene, debug tools, and other utilities.
     * @type {Object}
     */
    this.experience = new Experience();

    /**
     * Scene object where all lights and environment maps are added.
     * @type {THREE.Scene}
     */
    this.scene = this.experience.scene;

    /**
     * Debug tools for adjusting environment settings.
     * @type {Object}
     */
    this.debug = this.experience.debug;
    this.eventHub = this.experience.eventHub;

    // Lights and environment references
    this.lights = [];
    this.environmentMap = null;

    // Debug setup
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder("environment");
    }

    // Listen for view mode changes (3D/2D switching)
    this.eventHub.on("3d-enabled", async ({ enabled }) => {
      await this.switchEnvironment(enabled);
    });
  }

  cleanup() {
    this.#clearEnvironment();
    this.eventHub.off("3d-enabled", this.switchEnvironment);
  }

  // Central control for switching environments
  async switchEnvironment(enable3D) {
    log("Switching environment: ", enable3D ? "3D" : "2D");

    // Clear existing environment
    this.#clearEnvironment();

    if (enable3D) {
      // Set up 3D environment step-by-step
      this.#addKeyLight();
      this.#addFillLight();
      this.#addRimLight();
      this.#addTopDownLight();
      await this.#loadEnvironmentMap(); // HDRI for realistic reflections
    } else {
      // Set up 2D environment
      this.#setAmbientLight();
    }

    log("Environment switch complete.");
    this.eventHub.emit("environment-switched", { enabled3D: enable3D });
  }

  // # Clear current lights and environment
  #clearEnvironment() {
    this.lights.forEach((light) => this.scene.remove(light));
    this.lights = [];
    this.scene.environment = null;
    this.scene.background = null;
    log("Environment cleared.");
  }

  #addKeyLight() {
    const keyLight = new THREE.DirectionalLight("#ffffff", 2.0);
    keyLight.position.set(10, 20, 10);
    keyLight.castShadow = true;
    this.scene.add(keyLight);
    this.lights.push(keyLight);
  }
  #addFillLight() {
    const fillLight = new THREE.DirectionalLight("#ffffff", 1);
    fillLight.position.set(-10, 15, -10);
    this.scene.add(fillLight);
    this.lights.push(fillLight);
  }
  #addRimLight() {
    const rimLight = new THREE.PointLight("#ffffff", 1.2, 50);
    rimLight.position.set(0, 5, -15); // Behind the object
    this.scene.add(rimLight);
    this.lights.push(rimLight);
  }
  #addTopDownLight() {
    const topDownLight = new THREE.DirectionalLight("#ffffff", 1.5);
    topDownLight.position.set(0, 50, 0); // Directly above
    topDownLight.castShadow = true; // Enable shadows for depth
    this.scene.add(topDownLight);
    this.lights.push(topDownLight);
  }

  // # Load HDRI Environment Map
  async #loadEnvironmentMap() {
    const loader = new RGBELoader();
    try {
      const texture = await new Promise((resolve, reject) => {
        loader.load(
          "textures/environment/brown_photostudio_03_2k.hdr",
          (texture) => resolve(texture),
          undefined,
          (error) => reject(error)
        );
      });

      texture.mapping = THREE.EquirectangularReflectionMapping;
      this.environmentMap = texture;
      this.scene.environment = this.environmentMap;
      this.scene.background = new THREE.Color(0xeeeeee); // Neutral background
      this.experience.renderer.instance.toneMappingExposure = 1.5;

      log("Environment map applied successfully.");
    } catch (error) {
      log("Error loading HDRI:", error);
    }
  }

  /**
   * Updates materials in the scene to apply the current environment map and its intensity.
   */
  // # Add Ambient Light for 2D environment
  #setAmbientLight() {
    const ambientLight = new THREE.AmbientLight("#ffffff", 0.7);
    this.scene.add(ambientLight);
    this.lights.push(ambientLight);
    log("Ambient light added for 2D environment.");
  }
}
